import { useDispatch, useSelector } from 'react-redux';
import { selectLoanPortfolio, selectMaxAmount, selectMinAmount, setUpdateAmount } from '../../../../../features/gms-proposal-forms/amount-of-loans/amount-loans.slice';
import Label from '../../../../ui/label';
import Input from '../../../../ui/input';
import { formatNumberWithComma } from '../../../../../utils/format-numbers.utils';
import { customStyle, defaultStyle } from '../../../../../constant/custom-style';

export default function MinMaxPortfolioAmount() {
    const dispatch = useDispatch();

    const minAmount = useSelector(selectMinAmount);
    const maxAmount = useSelector(selectMaxAmount);
    const loanPortfolio = useSelector(selectLoanPortfolio);

    const fAmountMin = formatNumberWithComma(minAmount ?? '');
    const fAmountMax = formatNumberWithComma(maxAmount ?? '');
    const fAmountPortfolio = formatNumberWithComma(loanPortfolio ?? '');

    const isEmptyRange = minAmount === null || maxAmount === null;
    const hasRange = !isEmptyRange;

    const handleChange = (value: string, type: 'min' | 'max' | 'port') => {
        const cleaned = value.replace(/,/g, '').trim();
        const numericValue = cleaned === '' ? null : Number(cleaned);
        dispatch(setUpdateAmount({ type, amount: numericValue }));
    };

    return (
        <div className="grid w-full grid-cols-1 gap-3 rounded-md p-2 md:grid-cols-2">
            {/* MINIMUM / MAXIMUM */}
            <div className="min-w-0">
                <Label
                    label="Minimum and maximum amount of loans"
                    fontSize="text-xs"
                    fontWeight="font-semibold mb-1"
                    requiredText=" *"
                    isRequired={true}
                />

                <div className={`flex w-full items-center rounded-md border px-2 transition-colors focus-within:border-[#186cb2] ${hasRange ? 'border-[#186cb2] bg-[#186cb2]/10' : 'border-gray-300 bg-white'}`}>
                    {/* MINIMUM */}
                    <div className="min-w-0 flex-1">
                        <Input
                            type="text"
                            name="minAmount"
                            value={fAmountMin}
                            isCustomStyle={true}
                            customCss={`w-full border-0 bg-transparent px-1 py-2 text-xs outline-none placeholder:text-gray-400 ${fAmountMin ? customStyle : ''}`}
                            onChange={(e) => handleChange(e.target.value, 'min')}
                            required={true}
                            placeholder="Minimum"
                        />
                    </div>
                    <span className="shrink-0 px-1 text-xs text-gray-400">
                        -
                    </span>
                    {/* MAXIMUM */}
                    <div className="min-w-0 flex-1">
                        <Input
                            type="text"
                            name="maxAmount"
                            value={fAmountMax}
                            isCustomStyle={true}
                            customCss={`w-full border-0 bg-transparent px-1 py-2 text-right text-xs outline-none placeholder:text-gray-400 ${fAmountMax ? customStyle : ''}`}
                            onChange={(e) => handleChange(e.target.value, 'max')}
                            required={true}
                            placeholder="Maximum"
                        />
                    </div>
                </div>

                <div className="mt-1 flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gray-400">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>

                    <span className="text-[10px] leading-4 text-gray-400">
                        Enter the minimum and maximum allowable loan amount.
                    </span>
                </div>
            </div>

            {/* LOAN PORTFOLIO */}
            <div className="min-w-0">
                <Label
                    label="Loan Portfolio"
                    fontSize="text-xs"
                    fontWeight="font-semibold mb-1"
                    requiredText=" *"
                    isRequired={true}
                />

                <Input
                    type="text"
                    name="loanPortfolio"
                    value={fAmountPortfolio}
                    isCustomStyle={true}
                    customCss={`${defaultStyle} ${fAmountPortfolio ? customStyle : ''}`}
                    onChange={(e) => handleChange(e.target.value, 'port')}
                    required={true}
                    placeholder="Enter loan portfolio"
                />

                <div className="mt-1 flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gray-400">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>

                    <span className="text-[10px] leading-4 text-gray-400">
                        Enter the total loan portfolio amount.
                    </span>
                </div>
            </div>
        </div>
    );
}